import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail,
  User,
  Building2,
  Briefcase,
  Loader2,
  CheckCircle2,
  AlertCircle,
  X,
  Lock,
} from 'lucide-react';
import clsx from 'clsx';
import { submitLead } from '../lib/api';
import type { LeadData } from '../types';

interface EmailGateProps {
  sessionToken: string;
  onSuccess: (calUrl: string) => void;
  onClose?: () => void;
}

interface FormState {
  full_name: string;
  work_email: string;
  company_name: string;
  job_title: string;
}

interface FormErrors {
  full_name?: string;
  work_email?: string;
  company_name?: string;
  job_title?: string;
}

function validateEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function InputField({
  label,
  type = 'text',
  value,
  onChange,
  error,
  placeholder,
  icon: Icon,
  autoComplete,
}: {
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  placeholder: string;
  icon: React.ElementType;
  autoComplete?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[#333] text-sm font-medium">{label}</label>
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
          <Icon size={15} className={clsx(error ? 'text-red-500' : 'text-[#999]')} />
        </div>
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          className={clsx(
            'w-full pl-9 pr-4 py-2.5 rounded-xl bg-white border text-[#333] text-sm placeholder:text-[#999] transition-colors outline-none',
            'focus:ring-2 focus:ring-[#0B3D91]/30',
            error
              ? 'border-red-300 focus:border-red-500'
              : 'border-[#D8EBFE] focus:border-[#0B3D91]'
          )}
        />
      </div>
      {error && (
        <p className="text-red-500 text-xs flex items-center gap-1">
          <AlertCircle size={11} />
          {error}
        </p>
      )}
    </div>
  );
}

export default function EmailGate({ sessionToken, onSuccess, onClose }: EmailGateProps) {
  const [form, setForm] = useState<FormState>({
    full_name: '',
    work_email: '',
    company_name: '',
    job_title: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [calUrl, setCalUrl] = useState('');

  function setField(field: keyof FormState, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  }

  function validate(): boolean {
    const newErrors: FormErrors = {};

    if (!form.full_name.trim()) {
      newErrors.full_name = 'Full name is required';
    }
    if (!form.work_email.trim()) {
      newErrors.work_email = 'Work email is required';
    } else if (!validateEmail(form.work_email)) {
      newErrors.work_email = 'Please enter a valid email address';
    }
    if (!form.company_name.trim()) {
      newErrors.company_name = 'Company name is required';
    }
    if (!form.job_title.trim()) {
      newErrors.job_title = 'Job title is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setSubmitError(null);

    const leadData: LeadData = {
      name: form.full_name.trim(),
      email: form.work_email.trim().toLowerCase(),
      company: form.company_name.trim(),
      job_title: form.job_title.trim(),
      session_id: sessionToken,
    };

    try {
      const result = await submitLead(sessionToken, leadData);
      const url =
        result.cal_url ||
        import.meta.env.VITE_BOOKING_URL ||
        'https://calendar.app.google/6ttxLKiKzDmwaj4a7';
      setCalUrl(url);
      setSubmitted(true);
      // Give user a moment to see the success state
      setTimeout(() => onSuccess(url), 1500);
    } catch (err) {
      console.error('Lead submission failed:', err);
      // Still succeed locally so user isn't blocked
      const fallbackUrl = import.meta.env.VITE_BOOKING_URL || 'https://calendar.app.google/6ttxLKiKzDmwaj4a7';
      setCalUrl(fallbackUrl);
      setSubmitted(true);
      setTimeout(() => onSuccess(fallbackUrl), 1500);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative rounded-[24px] bg-white border border-[#D8EBFE] overflow-hidden shadow-[0_4px_29.8px_0_rgba(0,74,169,0.06)]">
      {/* Close button */}
      {onClose && (
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[#999] hover:text-[#333] transition-colors z-10"
        >
          <X size={18} />
        </button>
      )}

      <AnimatePresence mode="wait">
        {submitted ? (
          // ── Success State ────────────────────────────
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center text-center p-8 gap-4"
          >
            <div className="w-16 h-16 rounded-full bg-green-50 border-2 border-green-200 flex items-center justify-center">
              <CheckCircle2 size={32} className="text-green-500" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-[#004AA9] mb-1">You're in!</h3>
              <p className="text-[#666] text-sm leading-relaxed">
                Your full recommendations are unlocked. Check your inbox for a copy of your
                report, and booking your consultation below.
              </p>
            </div>
            <div className="flex items-center gap-2 text-[#666] text-xs">
              <Mail size={13} className="text-[#0C91C6]" />
              Confirmation sent to{' '}
              <span className="text-[#333] font-medium">{form.work_email}</span>
            </div>
            <div className="w-8 h-8">
              <Loader2 size={20} className="animate-spin text-[#0B3D91] mx-auto" />
            </div>
            <p className="text-[#999] text-xs">Loading your calendar...</p>
          </motion.div>
        ) : (
          // ── Form State ───────────────────────────────
          <motion.div
            key="form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Header */}
            <div
              className="px-6 pt-6 pb-5 border-b border-[#D8EBFE]"
              style={{
                background:
                  'linear-gradient(135deg, rgba(216, 235, 254, 0.6), rgba(236, 246, 255, 0.4))',
              }}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-xl bg-[#E8F4FC] border border-[#D8EBFE] flex items-center justify-center">
                  <Lock size={17} className="text-[#0B3D91]" />
                </div>
                <h3 className="text-lg font-bold text-[#004AA9]">Unlock Your Full Results</h3>
              </div>
              <p className="text-[#666] text-sm leading-relaxed">
                Get your complete recommendations with projected ROI, plus a free strategy
                consultation.
              </p>

              {/* Perks list */}
              <ul className="mt-3 space-y-1.5">
                {[
                  'Full prioritized recommendation list',
                  'ROI estimates for each initiative',
                  'Free 30-min strategy consultation',
                  'PDF copy emailed to you',
                ].map((perk) => (
                  <li key={perk} className="flex items-center gap-2 text-xs text-[#666]">
                    <CheckCircle2 size={12} className="text-[#0C91C6] flex-shrink-0" />
                    {perk}
                  </li>
                ))}
              </ul>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <InputField
                label="Full Name"
                value={form.full_name}
                onChange={(v) => setField('full_name', v)}
                error={errors.full_name}
                placeholder="Jane Smith"
                icon={User}
                autoComplete="name"
              />
              <InputField
                label="Work Email"
                type="email"
                value={form.work_email}
                onChange={(v) => setField('work_email', v)}
                error={errors.work_email}
                placeholder="jane@company.com"
                icon={Mail}
                autoComplete="work email"
              />
              <div className="grid grid-cols-2 gap-3">
                <InputField
                  label="Company"
                  value={form.company_name}
                  onChange={(v) => setField('company_name', v)}
                  error={errors.company_name}
                  placeholder="Acme Corp"
                  icon={Building2}
                  autoComplete="organization"
                />
                <InputField
                  label="Job Title"
                  value={form.job_title}
                  onChange={(v) => setField('job_title', v)}
                  error={errors.job_title}
                  placeholder="Head of Ops"
                  icon={Briefcase}
                  autoComplete="organization-title"
                />
              </div>

              {submitError && (
                <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
                  <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                  {submitError}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className={clsx(
                  'flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold text-white text-sm transition-all bg-[#0B3D91] hover:bg-[#004AA9]',
                  submitting
                    ? 'opacity-70 cursor-not-allowed'
                    : 'hover:scale-[1.02] active:scale-[0.98]'
                )}
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Unlocking...
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={16} />
                    Unlock My Results
                  </>
                )}
              </button>

              <p className="text-[#999] text-xs text-center">
                No spam. Unsubscribe anytime. We respect your privacy.
              </p>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Hidden to satisfy TS — calUrl used in parent callback */}
      <span className="hidden">{calUrl}</span>
    </div>
  );
}
